import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
    sessionMasters: [],
    currentSessionMaster: null,
    isLoading: false,
    error: null,
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all session masters
export const fetchSessionMasters = createAsyncThunk(
    'session/fetchSessionMasters',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/session-masters');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch session types');
        }
    }
);

// Fetch single session master by ID
export const fetchSessionMaster = createAsyncThunk(
    'session/fetchSessionMaster',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/session-masters/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch session type details');
        }
    }
);

// Create new session master
export const createSessionMaster = createAsyncThunk(
    'session/createSessionMaster',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post('/session-masters', formData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to create session type');
        }
    }
);

// Update session master
export const updateSessionMaster = createAsyncThunk(
    'session/updateSessionMaster',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/session-masters/${id}`, formData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to update session type');
        }
    }
);

// Delete session master
export const deleteSessionMaster = createAsyncThunk(
    'session/deleteSessionMaster',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/session-masters/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete session type');
        }
    }
);

/* ==============================
   SLICE
============================== */

const sessionSlice = createSlice({
    name: 'sessions',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentSessionMaster: (state, action) => {
            state.currentSessionMaster = action.payload;
        },
        clearCurrentSessionMaster: (state) => {
            state.currentSessionMaster = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchSessionMasters.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionMasters.fulfilled, (state, action) => {
                state.isLoading = false;
                state.sessionMasters = action.payload;
            })
            .addCase(fetchSessionMasters.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentSessionMaster = action.payload;
            })
            .addCase(fetchSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                if (!action.payload) return;
                state.sessionMasters.unshift(action.payload);
                state.currentSessionMaster = action.payload;
            })
            .addCase(createSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedData = action.payload.data || action.payload;
                if (!updatedData) return;

                const index = state.sessionMasters.findIndex((f) => f.id === updatedData.id);
                if (index !== -1) {
                    state.sessionMasters[index] = updatedData;
                }
                if (state.currentSessionMaster?.id === updatedData.id) {
                    state.currentSessionMaster = updatedData;
                }
            })
            .addCase(updateSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                state.sessionMasters = state.sessionMasters.filter((f) => f.id !== action.payload);
                if (state.currentSessionMaster?.id === action.payload) {
                    state.currentSessionMaster = null;
                }
            })
            .addCase(deleteSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentSessionMaster, clearCurrentSessionMaster } = sessionSlice.actions;
export default sessionSlice.reducer;
